import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { type PropsWithChildren, useRef, useState } from "react";
import { useSnapshot } from "valtio";

import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-regular-svg-icons";
import { LeadRowCard } from "../dashboard/home/components/card/LeadRowCard";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  Input,
  isEmailOrEmpty,
  isPhoneNumberOrEmpty,
  isString,
} from "../../components";

import {
  faAt,
  faGlobe,
  faPerson,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { membersState } from "../../state/members";
import { useAddOrganizationProfile } from "../../services/organtization/add-organization-profile";
import { SalesContactDrawerHeader } from "./SalesContactDrawerHeader";
import { salesContactState } from "../../state/salesContact";
import { ProfileContactDetails } from "../../client";
import { isURL } from "validator";
import { useCreateSalesContact } from "../../services/link/create-sales-contact";
import { ImageUpload } from "../../types";
import { ImageCropperDialog } from "../../components/ImageCropperDialog";
import { updateContactLogo } from "../../requests";

interface Props extends PropsWithChildren {
  onAddSalesContact: () => void;
  profileContactDetails?: ProfileContactDetails | null;
  profileLinkId?: string;
}

export const SalesContactDrawer = ({
  onAddSalesContact,
  profileContactDetails,
  profileLinkId,
  children,
}: Props) => {
  const toastId = useRef<string | number>();

  const [email, setEmail] = useState<string>("");
  const [logoAssetId, setLogoAssetId] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [salesContact, setSalesContact] = useState<string>("");
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");

  const { showAddSalesContact } = useSnapshot(salesContactState);

  const createSalesContact = useCreateSalesContact();

  const handleCreate = () => {
    if (!profileLinkId || !profileContactDetails) {
      toast.error("Missing required information");
      return;
    }

    // Use state values or fallback to profileContactDetails
    const finalEmail = email || profileContactDetails.email || "";
    const finalCompanyName = companyName || profileContactDetails.name || "";
    const finalWebsite = website || profileContactDetails.website || "";
    const finalPhoneNumber =
      phoneNumber || profileContactDetails.phoneNumber || "";
    const finalSalesContact =
      salesContact || profileContactDetails.salesContact || "";
    const finalLogoId = logoAssetId || profileContactDetails.logoId || "";
    // Validate required fields
    if (!finalEmail || !isEmailOrEmpty(finalEmail)) {
      toast.error("Valid email is required");
      return;
    }

    if (!finalCompanyName) {
      toast.error("Company name is required");
      return;
    }

    if (!finalWebsite) {
      toast.error("Website is required");
      return;
    }

    const contactData = {
      name: finalCompanyName,
      email: finalEmail,
      website: finalWebsite,
      phoneNumber: finalPhoneNumber,
      logoId: finalLogoId,
      ...(finalSalesContact ? { salesContact: finalSalesContact } : {}),
    };

    createSalesContact.mutate(
      {
        path: { profileLink: profileLinkId },
        body: contactData,
      },
      {
        onSuccess: () => {
          toast.success("Sales contact created successfully");
          onAddSalesContact();
          handleClose();
        },
        onError: (error) => {
          console.error("Error creating sales contact:", error);
          toast.error("Failed to create sales contact");
        },
      },
    );
  };

  const handleClose = () => {
    salesContactState.showAddSalesContact = false;
  };

  return (
    <Drawer
      direction="right"
      open={!!showAddSalesContact}
      onClose={handleClose}
      shouldScaleBackground={false}
    >
      {children}

      <DrawerContent
        className="min-w-[60%] max-w-[60%] flex flex-col"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Edit link</DrawerTitle>
          <DrawerDescription>
            Edit the link for the Lead Profile
          </DrawerDescription>
        </VisuallyHidden.Root>

        <SalesContactDrawerHeader
          handleCreateClick={handleCreate}
          handleClose={handleClose}
          emailIsValid={true}
        />
        {profileContactDetails && (
          <div className="flex flex-col justify-center mt-4 px-8">
            <div className="flex flex-col justify-center items-start ">
              <LeadRowCard className="text-xs bg-gray-2 my-4" type="solid">
                <div className="flex flex-1 items-center gap-3">
                  <FontAwesomeIcon
                    width={14}
                    height={14}
                    icon={faBuilding}
                    className="w-4 h-4 shrink-0"
                  />
                  <p>
                    Update the information to fit your brand and sales contact
                    needs.
                  </p>
                </div>
              </LeadRowCard>
              <div className="flex justify-center items-center">
                <img
                  alt="logo"
                  src={`https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/${logoAssetId !== "" ? logoAssetId : profileContactDetails.logoId}`}
                  className={`object-scale-contain`}
                  style={{
                    maxHeight: 100,
                  }}
                />
                <Button
                  className="ml-4"
                  onClick={() => {
                    document.getElementById("sales-logo-uploader")?.click();
                  }}
                >
                  Edit logo
                </Button>
              </div>
            </div>
            <div className="flex flex-col">
              <Input
                className="mt-8"
                placeholder="Company name"
                leftIcon={faBuilding}
                value={companyName || profileContactDetails.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCompanyName(e.target.value);
                }}
                validate={isString}
              />
              <Input
                className="mt-8"
                placeholder="Company website"
                leftIcon={faGlobe}
                value={website || profileContactDetails.website}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setWebsite(e.target.value);
                }}
                validate={isURL}
              />
              <Input
                className="mt-8"
                placeholder="Phone number"
                leftIcon={faPhone}
                value={phoneNumber || profileContactDetails.phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPhoneNumber(e.target.value);
                }}
                validate={isPhoneNumberOrEmpty}
              />
              <Input
                className="mt-8"
                placeholder="Sales contact name"
                leftIcon={faPerson}
                value={salesContact || profileContactDetails.salesContact}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSalesContact(e.target.value);
                }}
                validate={isString}
              />
              <Input
                className="mt-8"
                placeholder="Contact email"
                leftIcon={faAt}
                value={email || profileContactDetails.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const email = e.target.value;
                  setEmail(email);
                }}
                validate={isEmailOrEmpty}
              />
            </div>
            <input
              className="hidden"
              type="file"
              id={"sales-logo-uploader"}
              onChange={(event) => {
                if (event.target.files === null) return;

                const file = event.target.files[0];
                const reader = new FileReader();

                reader.onload = (e: ProgressEvent<FileReader>) => {
                  const formatInfo = (e.target?.result as string).split(",")[0];
                  const base64String = (e.target?.result as string).split(
                    ",",
                  )[1];
                  const format = formatInfo.split(";")[0].split(":")[1];

                  const imageUpload: ImageUpload = {
                    fileType: formatInfo,
                    base64File: base64String,
                  };

                  setImageToCrop(imageUpload.base64File);
                  setShowCropper(true);
                };

                reader.readAsDataURL(file);
              }}
            ></input>
            {showCropper && (
              <ImageCropperDialog
                open={showCropper}
                onClose={() => setShowCropper(false)}
                image={imageToCrop}
                onCrop={(croppedImage: string) => {
                  const imageUpload: ImageUpload = {
                    fileType: "image/jpeg",
                    base64File: croppedImage.split(",")[1],
                  };

                  // Make API call to update profile photo
                  updateContactLogo(profileLinkId!, imageUpload)
                    .then((photoId) => {
                      // Update the profile with the new photo ID
                      setLogoAssetId(photoId);
                      setShowCropper(false);
                    })
                    .catch((error) => {
                      console.error("Error updating profile photo:", error);
                    });
                }}
                aspectRatio={1}
              />
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
